import React from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';

/**
 * „Co robią te przyciski" — ściąga do paska akcji zbiorczej w Budynkach (właściciel 2026-10-02,
 * docx: „Przyciski na górze – do wytłumaczenia"). Jedno–dwa zdania prostym językiem na przycisk
 * i kolumna „Czy odwracalne" — żeby było jasne, które kliknięcie coś zmienia, a które tylko
 * pobiera plik. Treść trzyma się tego, co przyciski FAKTYCZNIE robią (PipelineView) — zmieniasz
 * akcję w pasku, zmień wiersz tutaj.
 *
 * A17 (Bronek 16.10, „białe przyciski przy Kawika"): druga tabela opisuje przyciski w WIERSZU budynku
 * (kolumna „Akcje" w PipelineView) — tam się wystawia, akceptuje i poprawia protokół. Te same zasady:
 * zmieniasz warunek albo działanie przycisku w wierszu, zmień opis w ROW_HELP_ROWS.
 */
export interface BulkHelpRow {
  /** Napis przycisku (bez licznika w nawiasie). */
  button: string;
  what: string;
  reversible: string;
}

export const BULK_HELP_ROWS: BulkHelpRow[] = [
  {
    button: 'Zaplanuj dla zaznaczonych',
    what: 'Zakłada przegląd z I (i ewentualnie II) terminem dla zaznaczonych budynków. Dla budynków, które już mają przegląd, może zmienić termin.',
    // Uwaga: dla budynku z istniejącym przeglądem akcja NADPISUJE termin (PUT rescheduleActive);
    // „Odplanuj" skasowałby wtedy cały przegląd, więc nie jest cofnięciem zmiany terminu.
    reversible: 'Nowy przegląd — tak: „Odplanuj", dopóki nikt nie zapisał lokalu i nie ma protokołu w CEEB. Zmianę terminu istniejącego przeglądu cofa tylko ponowne wpisanie starego terminu („Odplanuj" usunąłby cały przegląd).',
  },
  {
    // Migr. 073 (właściciel 21.10, „Zaplanuj do kolejki”) — zastępuje dawne „Odłóż zaznaczone”.
    button: 'Do kolejki',
    what: 'Dodaje zaznaczone budynki z etapu „Nowy” (albo „Zamknięty” z poprzedniego roku) do kolejki — BEZ terminów. Od razu są na tabletach; I termin ustawi się sam przy pierwszym lokalu zapisanym w terenie. Kolejkę widać pod chipem „W kolejce”, terminy można też zaplanować później („Zaplanuj”).',
    reversible: 'Tak — „Usuń z kolejki”, dopóki w terenie nikt nie zapisał lokalu.',
  },
  {
    button: 'Usuń z kolejki',
    what: 'Wyjmuje zaznaczone budynki z kolejki — wracają do „Nowy” i znikają z tabletów. Nie zadziała, gdy w terenie zapisano już lokal (przegląd trwa).',
    reversible: 'Tak — dodaj ponownie „Do kolejki”.',
  },
  {
    button: 'Odplanuj zaznaczone',
    what: 'Usuwa zaplanowany przegląd — budynek wraca do etapu sprzed planowania (zwykle „Nowy"). Nie zadziała, gdy są już zapisy lokali albo protokół w CEEB.',
    reversible: 'Tak — zaplanuj ponownie (terminy trzeba wpisać od nowa).',
  },
  {
    button: 'Zapisz wersje robocze',
    what: 'NIEBIESKI. Bot po kolei wpisuje sprawdzone lokale zaznaczonych budynków do CEEB jako wersje robocze. Protokoły NIE są wystawiane ani akceptowane. Pomija budynki z protokołem już wystawionym („Wprowadzony”) — tam bot nic nie zmieni. Protokół wystawia niebieski „Wyślij do CEEB” albo „Finalizuj w CEEB” w wierszu budynku, akceptuje zielony „Akceptuj w CEEB”.',
    reversible: 'Częściowo — wersję roboczą można poprawić i wysłać ponownie, ale sama z CEEB nie zniknie.',
  },
  {
    button: 'Akceptuj w CEEB',
    what: 'ZIELONY, tylko superadmin. Bot po kolei akceptuje protokoły „Wprowadzony” (lokale, potem protokół) — status w CEEB: Zaakceptowany. Pomija budynki, w których dane któregoś lokalu z terenu nie weszły do CEEB: te akceptuje się pojedynczo w wierszu, po sprawdzeniu w CEEB.',
    reversible: 'NIE — akceptacji w CEEB nie da się cofnąć.',
  },
  {
    button: 'Pobierz → Pobierz komplet',
    what: 'Jeden ZIP do wysłania zarządcy: katalog na każdy budynek, a w nim protokoły z CEEB (rok wybrany obok „Pobierz protokoły CEEB”), podsumowanie kontroli i karty lokali z podpisami lokatorów. Podsumowanie jest bez źródeł ciepła, chyba że zaznaczysz „Dołącz informacje o źródłach ciepła”. Czego brakuje (np. budynek bez protokołu w CEEB), wypisuje plik pominiete.txt.',
    reversible: 'Nic nie zmienia — tylko pobiera pliki (protokoły odczytuje z CEEB).',
  },
  {
    button: 'Pobierz → Pobierz podsumowania',
    what: 'Plik ZIP z podsumowaniami kontroli (PDF), osobny plik na każdy adres — do wysłania zarządcy. Pola wyboru pod spodem dokładają stronę ze statusem i przebiegiem kontroli w lokalach oraz informacje o źródłach ciepła (domyślnie ich nie ma).',
    reversible: 'Nic nie zmienia — tylko pobiera plik.',
  },
  {
    button: 'Pobierz → Pobierz grafik',
    what: 'Plik Excela z terminami zaznaczonych budynków: jeden wiersz = jeden budynek, I i II termin w osobnych kolumnach (kolejne terminy w kolumnie „Kolejne terminy”). Budynek bez terminu nie trafia do pliku.',
    reversible: 'Nic nie zmienia — tylko pobiera plik.',
  },
  {
    button: 'Pobierz → Wyślij grafik e-mailem',
    what: 'Ten sam plik Excela co „Pobierz grafik”, wysłany z serwera jako załącznik. Adres podpowiada się sam, gdy wszystkie zaznaczone budynki mają tego samego zarządcę z e-mailem; temat i treść można zmienić. Gdy wysyłka e-mail jest na serwerze wyłączona, okno o tym mówi i nic nie wychodzi.',
    reversible: 'Wysłanego maila nie da się cofnąć. Dane budynków się nie zmieniają; wysyłka jest odnotowana w audycie.',
  },
  {
    button: 'Pobierz → Numery protokołów',
    what: 'Lista numerów spraw CEEB zaznaczonych budynków: z bieżącego roku, a gdy budynek go nie ma — ostatni znany numer z rokiem. Każdy numer raz, po adresie; budynki bez numeru osobno. Można skopiować (z adresami albo same numery) lub zapisać do Excela (CSV).',
    reversible: 'Nic nie zmienia — tylko pokazuje i pobiera.',
  },
  {
    button: 'Pobierz → Podsumowanie źródeł ciepła',
    what: 'Tabela: ile źródeł ciepła każdego typu jest w lokalach zaznaczonych budynków, z sumą. Można zapisać do Excela (CSV).',
    reversible: 'Nic nie zmienia — tylko pokazuje i pobiera.',
  },
  {
    button: 'Pobierz → Pobierz protokoły CEEB',
    what: 'Oficjalne PDF-y protokołów pobrane z CEEB dla wybranego roku (domyślnie bieżący; można wybrać „wszystkie lata").',
    reversible: 'Nic nie zmienia — tylko pobiera pliki z CEEB.',
  },
  {
    button: 'Niesprawdzone lokale',
    what: 'Lista lokali bez wyniku kontroli (nieobecność, odmowa, niesprawdzone) w zaznaczonych budynkach — do skopiowania albo do Excela.',
    reversible: 'Nic nie zmienia — tylko pokazuje i pobiera.',
  },
  {
    button: 'Przypisz zarządcę',
    what: 'Przypisuje zaznaczone budynki do wybranego zarządcy. O zmianę zarządcy w budynku, który już jakiegoś ma, zapyta osobno.',
    reversible: 'Tak — przypisz innego zarządcę albo odepnij budynek w zakładce Zarządcy.',
  },
  {
    button: 'Wyczyść zaznaczenie',
    what: 'Odznacza wszystkie budynki. Na budynkach nic się nie zmienia.',
    reversible: 'Tak — zaznacz ponownie.',
  },
];

/** Przyciski w wierszu budynku (kolumna „Akcje”) — tylko te, które zmieniają coś w CEEB albo w planie. */
export const ROW_HELP_ROWS: BulkHelpRow[] = [
  {
    button: 'Wyślij do CEEB',
    what: 'NIEBIESKI. Widać go, gdy lokale z danymi z terenu nie weszły jeszcze do CEEB, a protokół nie jest wystawiony. Bot wpisuje lokale i wystawia protokół (status „Wprowadzony”), potem można go zaakceptować zielonym „Akceptuj w CEEB”. Gdy bot zauważy coś do sprawdzenia, zostawia wersję roboczą i mówi dlaczego. Przy gotowej wersji roboczej, którą aplikacja już wysyłała, a wszystkie lokale są w CEEB: tylko zapisuje wersję roboczą i potwierdza pole „Uwagi” — protokołu nie wystawia.',
    reversible: 'Wersję roboczą można poprawić i wysłać ponownie. Wystawionego protokołu („Wprowadzony”) zwykła wysyłka już nie zmienia — tylko „Popraw wystawiony protokół w CEEB” albo ręcznie w CEEB.',
  },
  {
    button: 'Finalizuj w CEEB',
    what: 'NIEBIESKI. Widać go, gdy wszystkie lokale są już w CEEB, a protokół jest wersją roboczą z numerem sprawy. Po potwierdzeniu w okienku bot wystawia zapisaną wersję roboczą bez zmiany treści (status „Wprowadzony”) — potem zielony „Akceptuj w CEEB”.',
    reversible: 'Wystawienia nie da się cofnąć w aplikacji. Poprawka — „Popraw wystawiony protokół w CEEB” albo ręcznie w CEEB.',
  },
  {
    button: 'Akceptuj w CEEB',
    what: 'ZIELONY, tylko superadmin. Widać go przy protokole „Wprowadzony”, jeszcze niezaakceptowanym. Okienko najpierw sprawdza protokół, po potwierdzeniu bot akceptuje lokale, a potem protokół — status w CEEB: „Zaakceptowany”. To samo zbiorczo robi „Akceptuj w CEEB” na pasku.',
    reversible: 'NIE — akceptacji w CEEB nie da się cofnąć.',
  },
  {
    button: 'Akceptuj w CEEB ⚠',
    what: 'BIAŁY z czerwonym napisem, tylko superadmin. Protokół jest „Wprowadzony”, ale dane z terenu części lokali nie weszły do CEEB — tam jest coś innego (np. kopia z poprzedniej kontroli). Akceptacja zatwierdzi to, co jest w CEEB, a nie to, co kominiarz wpisał w aplikacji. Okienko wymaga zaznaczenia „Sprawdziłem w CEEB — te lokale zostały poprawione ręcznie…”. Takie budynki akceptuje się tylko pojedynczo (pasek je pomija).',
    reversible: 'NIE — akceptacji w CEEB nie da się cofnąć. Najpierw sprawdź lokale w CEEB albo popraw protokół.',
  },
  {
    button: 'Popraw wystawiony protokół w CEEB',
    what: 'BIAŁY, tylko superadmin i tylko przy włączonym przełączniku poprawek (CEEB_EDIT_ISSUED). Widać go przy protokole „Wprowadzony” z bieżącego roku, niezaakceptowanym, gdy dane z terenu części lokali nie weszły do CEEB. W okienku zaznaczasz lokale do wpisania i przepisujesz numer sprawy. Przed zmianą bot zapisuje pełną kopię protokołu z CEEB — bez udanej kopii niczego nie zmienia. Domyślnie najpierw próba bez zapisu (w CEEB nic się nie zmienia). Przy protokole zaakceptowanym przycisk jest wyszarzony (kłódka): poprawka tylko ręcznie w CEEB.',
    reversible: 'Nie da się cofnąć jednym kliknięciem — zostaje kopia protokołu sprzed zmiany (PDF w okienku), według niej można poprawić ręcznie w CEEB.',
  },
  {
    button: 'Zaplanuj I termin / Zaplanuj II termin',
    what: 'BIAŁY. Widać go, gdy w przeglądzie zostały lokale do powrotu, a przegląd nie ma jeszcze I albo II terminu. Dodaje termin (data i powód opcjonalnie) — nic nie wysyła do CEEB. Trzeci i dalszy termin: zaznacz budynek i „Zaplanuj dla zaznaczonych”.',
    reversible: 'Tak — termin usuwa się w szczegółach budynku („Otwórz”).',
  },
];

const th: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  textAlign: 'left',
  fontWeight: 'var(--weight-semibold)' as any,
  color: 'var(--text-heading)',
  borderBottom: 'var(--border-width-strong) solid var(--border-strong)',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  color: 'var(--text-primary)',
  borderBottom: 'var(--border-width) solid var(--border)',
  verticalAlign: 'top',
};

const sectionTitle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0',
  fontSize: 'var(--text-body)',
  fontWeight: 'var(--weight-semibold)' as any,
  color: 'var(--text-heading)',
};

function HelpTable({ rows, testId }: { rows: BulkHelpRow[]; testId: string }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid={testId} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
        <thead>
          <tr>
            <th style={th}>Przycisk</th>
            <th style={th}>Co robi</th>
            <th style={th}>Czy odwracalne</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.button}>
              <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any }}>{r.button}</td>
              <td style={td}>{r.what}</td>
              <td style={td}>{r.reversible}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PipelineBulkHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <ModalShell
      open={open}
      title="Co robią te przyciski"
      width={860}
      onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>Zamknij</Button>}
    >
      <div data-testid="pipeline-bulk-help" style={{ fontFamily: 'var(--font-sans)', display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Przyciski paska działają na budynkach zaznaczonych w okienku przed adresem.
        </p>
        <HelpTable rows={BULK_HELP_ROWS} testId="pipeline-bulk-help-toolbar" />
        <h3 style={sectionTitle}>Przyciski w wierszu budynku</h3>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Działają na jednym budynku i pokazują się tylko wtedy, gdy akcja ma sens — dlatego przy różnych
          budynkach widać różne przyciski. Protokół wystawia się tylko tutaj — pasek zapisuje wyłącznie wersje
          robocze. Akceptować można tutaj albo zbiorczo na pasku.
        </p>
        <HelpTable rows={ROW_HELP_ROWS} testId="pipeline-bulk-help-rows" />
      </div>
    </ModalShell>
  );
}
